import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicObject,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateSphereGeometryData,
  hsMakeGeometry,
  type HsCamera,
  type HsDefaultShader,
  type HsGraphics,
  type HsRegularGeometry,
  type HsShader,
} from '@sibe/hyperscene';

/** Declarations every custom fragment shader gets; the vertex stage must come from `addColor().addDepth()`. */
const FRAGMENT_PRELUDE: string = `#version 300 es
precision highp float;

uniform float u_logDepthBufFC;
uniform float u_time;

in vec4 v_position;
in vec3 v_color;
in float v_fragDepth;

layout(location = 0) out vec4 fragmentColor;
`;

const SKY_DOME_RADIUS: number = 100;

/**
 * A material that keeps the vertex stage built by `shaderData` and replaces the fragment stage with custom GLSL,
 * the current-engine equivalent of the legacy custom mesh and point shaders. `fragmentCode` holds functions and
 * `main()`; `fields` declares extra varyings of the vertex stage, such as `in vec4 v_point;`.
 */
export function makeCustomFragmentMaterial(
  gl: WebGL2RenderingContext,
  shaderData: HsShaderData,
  fragmentCode: string,
  fields: string = ''
): HsDefaultMaterial<HsDefaultShader> {
  return new HsDefaultMaterial(gl, setCustomFragment(shaderData.makeShader(), fragmentCode, fields));
}

/** Replaces the fragment stage of a built shader before a material compiles it; see `makeCustomFragmentMaterial`. */
export function setCustomFragment(shader: HsDefaultShader, fragmentCode: string, fields: string = ''): HsDefaultShader {
  // A replaced fragment stage also turns off mesh batching, which would rebuild the default code.
  shader.makeFragmentCode = (): string => `${FRAGMENT_PRELUDE}\n${fields}\n${fragmentCode}`;
  return shader;
}

/** Uploads the animation time of custom shaders that declare `u_time`. */
export function setShaderTime(gl: WebGL2RenderingContext, shaders: readonly HsShader[], seconds: number): void {
  for (const shader of shaders) {
    // The renderer selects its own program for every frame, so switching programs here is safe.
    shader.useProgram(gl);
    shader.uniform1f(gl, 'u_time', seconds);
  }
  gl.useProgram(null);
}

/**
 * A sky gradient with a sun drawn on a sphere around the camera, replacing the legacy background layer. It writes
 * the farthest depth, so every other object covers it. Keep it on the camera with `placeSkyDome`.
 */
export function makeSkyDome(graphics: HsGraphics): HsGraphicObject | null {
  const geometry: HsRegularGeometry | null = hsMakeGeometry(graphics, hsGenerateSphereGeometryData(16));
  if (geometry === null) {
    return null;
  }
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth().addRayIntersection();
  const material: HsDefaultMaterial<HsDefaultShader> = makeCustomFragmentMaterial(
    graphics.gl,
    shaderData,
    `
    void main() {
      gl_FragDepth = 0.99999;
      vec3 rd = normalize(v_point.xyz);
      float height = rd.y * 0.5 + 0.5;
      vec3 sky = vec3(0.4, 0.7, 1.0);
      vec3 sunColor = vec3(1.0, 0.5, 0.1);
      vec3 sunDir = normalize(vec3(-1.0, 1.0, -1.0));
      sky += pow(max(dot(rd, sunDir), 0.0), 512.0) * sunColor;
      vec3 horizon = vec3(1.0, 1.0, 1.0);
      vec3 ground = vec3(0.8, 0.75, 0.65);
      float skyGradient = smoothstep(0.7, 0.5, height);
      float groundGradient = smoothstep(0.5, 0.499, height);
      vec3 color = mix(sky, horizon, skyGradient);
      color = mix(color, ground, groundGradient);
      fragmentColor = vec4(color, 1.0);
    }
    `,
    'in vec4 v_point;'
  );
  const dome: HsGraphicObject = new HsGraphicObject(getUuid(), 'skyDome', geometry, material);
  dome.worldMatrix.scale([SKY_DOME_RADIUS, SKY_DOME_RADIUS, SKY_DOME_RADIUS]);
  dome.updateBoundingSphere();
  return dome;
}

/** Centers the sky dome on the camera; call it before every culling pass. */
export function placeSkyDome(dome: HsGraphicObject, camera: HsCamera): void {
  const cameraMatrix: readonly number[] = camera.worldMatrix.values;
  dome.worldMatrix.setPosition([cameraMatrix[12], cameraMatrix[13], cameraMatrix[14]]);
  dome.updateBoundingSphere();
}
